import { EventIds, ItemsPerPage, ItemsPerPageGuest } from 'constants/index';
import { withErrorBoundary } from 'error';
import React, { useRef } from 'react';
import { t } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import { ApolloError, useQuery } from '@apollo/client';
import { RepackBySlugOrGuid } from 'apollo/repack/repack.gql';
import PackDetailsCarousel from 'components/PackDetailsCarousel/PackDetailsCarousel';
import { PageBody } from 'components/PageLayout';
import TakeDownInfo from 'components/TakeDownInfo/TakeDownInfo';
import { getSanitizeSlug } from 'helpers/getSanitizeSlug';
import { useTakenDownPack } from 'hooks/useTakenDown/useTakenDown';
import PageHeaderForPackOrRepack from 'components/PageHeaderForPackOrRepack/PageHeaderForPackOrRepack';
import useUpdateErrorBoundary, {
    itemErrorGuard,
} from 'hooks/useUpdateErrorBoundary/useUpdateErrorBoundary';
import SearchProvider from 'controllers/SearchController/SearchContext';
import repackQueries from 'apollo/repack/repack';
import DetailsView from 'components/DetailsView/DetailsView';
import Filters from 'components/Filters/Filters';
import { useScrollToElement } from 'hooks';
import SearchQuery from 'controllers/SearchController/components/SearchQuery/SearchQuery';
import { RoutePaths } from 'types/enums';
import { PageBreadcrumbs } from 'components/PageBreadcrumbs/PageBreadcrumbs';
import {
    Repack,
    RepackBySlugOrGuidQuery,
    RepackBySlugOrGuidQueryVariables,
} from 'types/generated/graphql';
import { usePrerenderComponentsRequired } from 'hooks/usePrerenderComponentsRequired/usePrerenderComponentsRequired';
import { FreemiumGatingBanner } from 'components/FreemiumGatingBanner/FreemiumGatingBanner';
import { useFreemiumGatingContext } from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { DropdownSortingTypes } from 'components/DetailsView/components/useSortingOptionItem';
import { ArtistBanner } from 'components/ArtistBanner/ArtistBanner';
import { Breadcrumb } from 'components/PageBreadcrumbs/types';

const useRePackDetails = (
    slug?: string,
): {
    repack: Repack | undefined;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const { data, loading, error } = useQuery<
        RepackBySlugOrGuidQuery,
        RepackBySlugOrGuidQueryVariables
    >(RepackBySlugOrGuid, {
        skip: !getSanitizeSlug(slug),
        variables: { repackSlugOrGuid: getSanitizeSlug(slug) },
    });

    return { repack: data?.repack, loading, error };
};

const sortingOptions = [
    DropdownSortingTypes.Trending,
    DropdownSortingTypes.Recent,
    DropdownSortingTypes.NameAsc,
    DropdownSortingTypes.NameDesc,
];

const RepackDetails: React.FC = () => {
    usePrerenderComponentsRequired({
        componentNames: ['SamplesPane'],
    });

    const { slug } = useParams<{ slug: string }>();
    const { repack, loading, error } = useRePackDetails(slug);
    const { isTakenDown } = useTakenDownPack(repack);
    const { isAppGated } = useFreemiumGatingContext();

    const itemsPerPage = isAppGated ? ItemsPerPageGuest : ItemsPerPage;

    const pageBodyRef = useRef<HTMLDivElement>(null);
    const { scrollToElement: handlePageChanged } =
        useScrollToElement(pageBodyRef);

    const didError = useUpdateErrorBoundary({
        getDidError: itemErrorGuard<Repack>(loading, error, repack),
        message: `RepackDetail render null`,
        eventId: EventIds.PackDetailsRenderNull,
        error,
    });

    if (didError) {
        return null;
    }

    const breadcrumbs: Breadcrumb[] = [
        { route: RoutePaths.ForYou, label: t`Home` },
        {
            route: RoutePaths.Collections,
            label: t`Collections`,
        },
        {
            route: RoutePaths.Category,
            wildcard: repack?.brandtype?.slug,
            label: repack?.brandtype?.name,
        },
        {
            route: RoutePaths.Collections,
            wildcard: repack?.slug,
            label: repack?.name,
            isCurrent: true,
        },
    ];

    const queries = repackQueries(repack && repack.guid);

    return (
        <>
            <PageHeaderForPackOrRepack
                item={repack}
                isTakenDown={isTakenDown}
                isLoading={loading || !repack}
            />
            <PageBody>
                {isTakenDown ? (
                    <TakeDownInfo />
                ) : (
                    <SearchProvider samplesPerPage={itemsPerPage}>
                        <SearchQuery samplesQueryConfig={queries.samples} />
                        <Filters
                            defaultPlaceholder={t`Search within collection`}
                        >
                            <Filters.SearchBar />
                            <Filters.SuggestionsPills />
                        </Filters>
                        <DetailsView>
                            <DetailsView.DropdownSorting
                                sortingOptions={sortingOptions}
                            />
                            <DetailsView.SamplesPane
                                onPageChanged={handlePageChanged}
                            />
                        </DetailsView>
                    </SearchProvider>
                )}
                {repack && <PackDetailsCarousel item={repack} />}
                <ArtistBanner />
                {isAppGated && <FreemiumGatingBanner />}
            </PageBody>
            <PageBreadcrumbs breadcrumbs={breadcrumbs} />
        </>
    );
};

export default withErrorBoundary(RepackDetails, 'page');
